// PersonaZone — how one person works with us, in their own words (§10, §13).
//
// A persona is agent-written prose, so it carries a claim mark: ◇ while it is only proposed,
// ▪ once an operator has held it. Every trait has at least one verbatim quote, and the quote is
// the thing that makes the trait readable at all — so it is one click away, never hidden and
// never summarised.
//
// "Synthesise" starts a RUN. It writes nothing: the run reads the interactions, the mail and
// the dossier and files a `persona` proposal that waits in Review. The zone says so after the
// click rather than pretending a persona appeared.
import { useCallback, useEffect, useState } from 'react';
import { Att, Ev, Fig, Record, Ref, Sep, Sheet, Tag, Zone, ZoneHead } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open } from '../routes.js';
import { personaFor, synthesise } from '../api/personas.js';
import { ApiError } from '../api/client.js';
import { useToast } from './Toast.js';
import './PersonaZone.de.js';
import './PersonaZone.fr.js';
import type { Persona, Trait } from '../../shared/types.js';

export interface PersonaZoneProps {
  personId: number;
}

/** The six keys the skill may file, in the order a reader wants them. */
const TRAIT_ORDER = ['decision_role', 'topics', 'objections', 'style', 'register', 'timing'];

const TRAIT_LABEL: Record<string, string> = {
  topics: 'Topics',
  style: 'Style',
  decision_role: 'Decision role',
  objections: 'Objections',
  register: 'Register',
  timing: 'Timing',
};

function ordered(traits: Trait[]): Trait[] {
  return [...(traits ?? [])].sort((a, b) => {
    const ia = TRAIT_ORDER.indexOf(String(a.key));
    const ib = TRAIT_ORDER.indexOf(String(b.key));
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
}

export default function PersonaZone({ personId }: PersonaZoneProps) {
  const t = useT();
  const { fmtDate } = useLocale();
  const toast = useToast();

  const [persona, setPersona] = useState<Persona | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPersona(await personaFor(personId));
      setState('ready');
      setError(null);
    } catch (err) {
      // A failed read is silence, never an empty persona.
      setState('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [personId]);

  useEffect(() => { void load(); }, [load]);

  const run = useCallback(async () => {
    setBusy(true);
    try {
      const answer = await synthesise(personId);
      setStarted(answer.run_ref);
      toast('ok', t('{ref} started. It reads the record and files a proposal; nothing is written until you hold it.', { ref: answer.run_ref }));
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not start the run.'));
    } finally {
      setBusy(false);
    }
  }, [personId, t, toast]);

  const held = persona?.status === 'held';
  const traits = persona ? ordered(persona.traits) : [];

  return (
    <Zone>
      <ZoneHead
        label={t('Persona')}
        count={persona ? <Fig>{traits.length}</Fig> : undefined}
        note={persona
          ? t('version {n} · {status} · every trait carries a verbatim quote', {
            n: String(persona.version),
            status: held ? t('held') : t('proposed'),
          })
          : t('synthesised from the held interactions, the mail and the dossier')}
        route={
          <button type="button" className="zone-route" disabled={busy} onClick={() => { void run(); }}>
            {busy ? t('Starting…') : t('Synthesise')} →
          </button>
        }
      />
      <Sheet clip={!!traits.length} pad={!traits.length}>
        {state === 'loading' ? (
          <p className="zone-empty">{t('loading…')}</p>
        ) : state === 'error' ? (
          <p className="zone-empty">{t('The persona could not be read: {error}', { error: error ?? '' })}</p>
        ) : !persona ? (
          <p className="zone-empty">
            {started
              ? t('{ref} is running. When it finishes, its persona waits in Review.', { ref: started })
              : t('No persona yet. Synthesise reads the held interactions, the mail and the dossier and proposes traits — topics, style, decision role, objections, register, timing — each carrying at least one verbatim quote. A trait without a quote is dropped before the proposal is filed, and the proposal is held in Review like any other.')}
          </p>
        ) : (
          <>
            {traits.map((trait) => {
              const key = String(trait.key);
              const quotes = (trait.evidence ?? []).filter((e) => e.quote);
              const isOpen = openKey === key;
              return (
                <div key={key} className="row" style={{ gridTemplateColumns: '140px 1fr 120px' }}>
                  <span className="u-label">{t(TRAIT_LABEL[key] ?? key)}</span>
                  <span>
                    <Record>
                      <Att kind={held ? 'held' : 'proposed'} /> {trait.value}
                    </Record>
                    {isOpen && quotes.map((e, i) => (
                      <Ev
                        key={i}
                        src={<>
                          <Att kind="quoted" />
                          {e.ref ? <Ref>{e.ref}</Ref> : <span className="d">{t('no source named')}</span>}
                          <Sep />
                          {t(e.kind ?? 'manual')}
                        </>}
                      >
                        {e.quote}
                      </Ev>
                    ))}
                  </span>
                  <span className="d">
                    {quotes.length ? (
                      <button type="button" className="zone-route" onClick={() => setOpenKey(isOpen ? null : key)}>
                        {isOpen ? t('Hide quotes') : t('{n} quotes', { n: String(quotes.length) })}
                      </button>
                    ) : (
                      <Tag tone="ghost">{t('no quote')}</Tag>
                    )}
                  </span>
                </div>
              );
            })}
            <div className="row" style={{ gridTemplateColumns: '1fr auto' }}>
              <span className="d">
                {persona.principal ? <>{t('filed by')} <Ref>{persona.principal}</Ref><Sep /></> : null}
                <Fig>{fmtDate(persona.created_at)}</Fig>
                {persona.run_id ? <><Sep /><Ref>{`run:${persona.run_id}`}</Ref></> : null}
              </span>
              <span>
                {held
                  ? <Tag tone="ok"><Att kind="held" /> {t('held')}</Tag>
                  : (
                    <button type="button" className="zone-route" onClick={() => open(ROUTES.review({ kind: 'persona' }))}>
                      {t('Hold it in Review')} →
                    </button>
                  )}
              </span>
            </div>
          </>
        )}
      </Sheet>
    </Zone>
  );
}
